import { useState } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Space,
  Typography,
  Dropdown,
  Modal,
  Empty,
  Grid,
  Divider,
  Tag,
  Row,
  Col,
  Avatar,
  theme,
  Select
} from 'antd';
import { X } from 'lucide-react';
import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  FileTextOutlined,
  CalendarOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { Tenant, CreateTenantData, UpdateTenantData } from '@/features/superadmin/types';
import { TenantForm } from './TenantForm';
import { DeleteTenantDialog } from './DeleteTenantDialog';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface TenantListProps {
  tenants: Tenant[];
  onToggleStatus: (tenant_id: string, active: boolean) => Promise<void>;
  onCreateTenant: (data: CreateTenantData) => Promise<any>;
  onUpdateTenant: (id: string, data: UpdateTenantData) => Promise<any>;
  onDeleteTenant: (id: string) => Promise<any>;
}

export function TenantList({
  tenants,
  onToggleStatus,
  onCreateTenant,
  onUpdateTenant,
  onDeleteTenant
}: TenantListProps) {
  const { token } = theme.useToken();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [searchTerm, setSearchTerm] = useState('');
  const [tenantToToggle, setTenantToToggle] = useState<{ id: string; nome: string; status: string } | null>(null);

  // Estados para o modal de criação
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Estados para o modal de edição
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tenantToEdit, setTenantToEdit] = useState<Tenant | null>(null);

  // Estados para o modal de exclusão
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [statusFilter, setStatusFilter] = useState<'todos' | 'ATIVO' | 'INATIVO'>('todos');

  // Filtragem das empresas com base na busca e status
  const filteredTenants = tenants.filter((tenant) => {
    const matchNome = tenant.nome.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchNome) return false;
    if (statusFilter === 'todos') return true;
    const status = tenant.status || (tenant.ativo ? 'ATIVO' : 'INATIVO');
    return status === statusFilter;
  });

  // Formato de data 
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Handler para abrir o modal de edição
  const handleEditClick = (tenant: Tenant) => {
    setTenantToEdit(tenant);
    setIsEditModalOpen(true);
  };

  // Handler para abrir o modal de exclusão
  const handleDeleteClick = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setIsDeleteModalOpen(true);
  };

  // Handler para submeter o formulário de criação
  const handleCreateSubmit = async (data: CreateTenantData) => {
    await onCreateTenant(data);
    setIsCreateModalOpen(false);
  };

  // Handler para submeter o formulário de edição
  const handleEditSubmit = async (data: UpdateTenantData) => {
    if (!tenantToEdit) return;
    await onUpdateTenant(tenantToEdit.id, { ...data, id: tenantToEdit.id });
    setIsEditModalOpen(false);
    setTenantToEdit(null);
  };

  // Handler para confirmar a exclusão
  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;

    setIsDeleting(true);
    try {
      await onDeleteTenant(tenantToDelete.id);
    } catch (error) {
      console.error('Erro ao excluir empresa:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setTenantToDelete(null);
    }
  };

  const getTenantStatus = (tenant: Tenant): string => {
    return tenant.status || (tenant.ativo ? 'ATIVO' : 'INATIVO');
  };

  const columns = [
    {
      title: 'Empresa',
      key: 'nome',
      render: (_: any, record: Tenant) => (
        <Space>
          {record.logo_url ? (
            <Avatar
              src={record.logo_url}
              shape="square"
              icon={<BankOutlined />}
              style={{ backgroundColor: '#f0f2f5' }}
            />
          ) : (
            <Avatar
              shape="square"
              icon={<BankOutlined />}
              style={{ backgroundColor: '#e6f7ff', color: '#1890ff' }}
            />
          )}
          <Text strong>{record.nome}</Text>
        </Space>
      ),
    },
    {
      title: 'Tipo',
      key: 'tipo_unidade',
      render: (_: any, record: Tenant) => {
        const tipo = record.tipo_unidade;
        if (!tipo) return <Text type="secondary">—</Text>;
        const colors: Record<string, string> = {
          MATRIZ: 'purple',
          ASSOCIADO: 'blue',
          REPRESENTACAO: 'orange',
        };
        const labels: Record<string, string> = {
          MATRIZ: 'Matriz',
          ASSOCIADO: 'Associado',
          REPRESENTACAO: 'Representação',
        };
        return <Tag color={colors[tipo] ?? 'default'}>{labels[tipo] ?? tipo}</Tag>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: Tenant) => {
        const status = getTenantStatus(record);
        return (
          <Tag color={status === 'ATIVO' ? 'success' : status === 'INATIVO' ? 'error' : 'warning'}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Usuários',
      key: 'users_count',
      render: (_: any, record: Tenant) => (
        <Space>
          <TeamOutlined />
          {record._count?.users || 0}
        </Space>
      ),
    },
    {
      title: 'Contratos',
      key: 'contracts_count',
      render: (_: any, record: Tenant) => (
        <Space>
          <FileTextOutlined />
          {record._count?.contratos || 0}
        </Space>
      ),
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date),
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: Tenant) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Editar',
                icon: <EditOutlined />,
                onClick: () => handleEditClick(record)
              },
              {
                key: 'toggle',
                label: getTenantStatus(record) === 'ATIVO' ? 'Desativar' : 'Ativar',
                icon: getTenantStatus(record) === 'ATIVO' ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                onClick: () => setTenantToToggle({
                  id: record.id,
                  nome: record.nome,
                  status: getTenantStatus(record)
                })
              },
              {
                type: 'divider'
              },
              {
                key: 'delete',
                label: 'Excluir',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleDeleteClick(record)
              }
            ]
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Row gutter={[16, 16]} align="middle" justify="space-between">
        <Col xs={24} md={16}>
          <Space wrap style={{ width: '100%' }}>
            <Input
              placeholder="Buscar empresa por nome..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{ minWidth: 220, flex: 1 }}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: 'todos', label: 'Todos os status' },
                { value: 'ATIVO', label: 'Ativas' },
                { value: 'INATIVO', label: 'Inativas' },
              ]}
            />
          </Space>
        </Col>
        <Col xs={24} md={8} style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalOpen(true)}
            block={isMobile}
          >
            Nova Empresa
          </Button>
        </Col>
      </Row>

      <Card variant="borderless" styles={{ body: { padding: 0 } }}>
        {isMobile ? (
          <div style={{ padding: '0 16px' }}>
            {filteredTenants.length === 0 ? (
              <Empty description="Nenhuma empresa encontrada" style={{ padding: '40px 0' }} />
            ) : (
              filteredTenants.map((tenant: Tenant) => (
                <div
                  key={tenant.id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    borderRadius: '12px',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    boxShadow: token.boxShadowTertiary
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Space>
                      {tenant.logo_url ? (
                        <Avatar
                          src={tenant.logo_url}
                          shape="square"
                          size={40}
                          icon={<BankOutlined />}
                          style={{ backgroundColor: '#f0f2f5' }}
                        />
                      ) : (
                        <Avatar
                          shape="square"
                          size={40}
                          icon={<BankOutlined />}
                          style={{ backgroundColor: '#e6f7ff', color: '#1890ff' }}
                        />
                      )}
                      <Text strong>{tenant.nome}</Text>
                    </Space>
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          {
                            key: 'toggle',
                            label: getTenantStatus(tenant) === 'ATIVO' ? 'Desativar' : 'Ativar',
                            icon: getTenantStatus(tenant) === 'ATIVO' ? <CloseCircleOutlined /> : <CheckCircleOutlined />,
                            onClick: () => setTenantToToggle({
                              id: tenant.id,
                              nome: tenant.nome,
                              status: getTenantStatus(tenant)
                            })
                          },
                          {
                            key: 'delete',
                            label: 'Excluir',
                            icon: <DeleteOutlined />,
                            danger: true,
                            onClick: () => handleDeleteClick(tenant)
                          }
                        ]
                      }}
                    >
                      <Button type="text" icon={<MoreOutlined />} />
                    </Dropdown>
                  </div>

                  <div style={{ width: '100%' }}>
                    <Space orientation="vertical" size={0}>
                      <Space separator={<Divider orientation="vertical" />}>
                        <Tag color={getTenantStatus(tenant) === 'ATIVO' ? 'success' : 'error'}>
                          {getTenantStatus(tenant)}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <CalendarOutlined /> {formatDate(tenant.created_at)}
                        </Text>
                      </Space>
                      <Space size="middle" style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <TeamOutlined /> {tenant._count?.users || 0} usuários
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          <FileTextOutlined /> {tenant._count?.contratos || 0} contratos
                        </Text>
                      </Space>
                    </Space>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredTenants}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="Nenhuma empresa cadastrada" /> }}
          />
        )}
      </Card>

      <Modal
        open={!!tenantToToggle}
        onCancel={() => setTenantToToggle(null)}
        footer={null}
        closable={false}
        width={600}
        destroyOnHidden
        centered
        styles={{
          mask: { background: 'rgba(0, 0, 0, 0.45)' },
          body: { padding: 0, overflow: 'hidden' },
        }}
        zIndex={1000}
      >
        <button
          type="button"
          onClick={() => setTenantToToggle(null)}
          className="absolute top-4 right-4 z-50 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-gray-400 transition"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col h-auto bg-white dark:bg-zinc-900">
          <div className={`${tenantToToggle?.status === 'ATIVO' ? 'bg-[#ff4d4f] dark:bg-red-900' : 'bg-[#52c41a] dark:bg-green-900'} text-white px-6 py-4 flex-shrink-0`}>
            <p className="text-[11px] text-white/50 uppercase tracking-wide mb-1">
              {tenantToToggle?.status === 'ATIVO' ? 'Desativar' : 'Ativar'} Empresa
            </p>
            <h2 className="text-[15px] font-semibold">
              {tenantToToggle?.status === 'ATIVO' ? 'Desativar' : 'Ativar'} "{tenantToToggle?.nome}"
            </h2>
          </div>

          <div className="px-6 py-6 flex-1 overflow-y-auto max-h-[70vh]">
            <div className={`p-4 rounded-lg border mb-4 ${tenantToToggle?.status === 'ATIVO' ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900' : 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900'}`}>
              <p className="text-gray-900 dark:text-white margin-0">
                Você tem certeza que deseja {tenantToToggle?.status === 'ATIVO' ? 'desativar' : 'ativar'} a empresa <strong>{tenantToToggle?.nome}</strong>?
              </p>
            </div>

            {tenantToToggle?.status === 'ATIVO' && (
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 mb-4">
                <div className="flex gap-3">
                  <ExclamationCircleOutlined className="text-red-600 dark:text-red-400 flex-shrink-0 text-lg mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900 dark:text-red-200 margin-0">
                      Os usuários desta empresa não poderão mais acessar o sistema até que seja reativada.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-8 pt-4 border-t border-gray-200 dark:border-zinc-700">
              <Button onClick={() => setTenantToToggle(null)} style={{ flex: 1 }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                danger={tenantToToggle?.status === 'ATIVO'}
                onClick={() => {
                  if (tenantToToggle) {
                    onToggleStatus(tenantToToggle.id, tenantToToggle.status !== 'ATIVO');
                    setTenantToToggle(null);
                  }
                }}
                style={{ flex: 1, ...(tenantToToggle?.status !== 'ATIVO' && { backgroundColor: '#52c41a' }) }}
              >
                {tenantToToggle?.status === 'ATIVO' ? 'Desativar Empresa' : 'Ativar Empresa'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <TenantForm
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        title="Nova Empresa"
        description="Preencha os dados para criar uma nova empresa no sistema."
        submitLabel="Criar Empresa"
      />

      {tenantToEdit && (
        <TenantForm
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setTenantToEdit(null);
          }}
          onSubmit={handleEditSubmit}
          initialData={{
            nome: tenantToEdit.nome,
            status: getTenantStatus(tenantToEdit) as any,
            tipo_unidade: tenantToEdit.tipo_unidade ?? undefined,
            logo_url: tenantToEdit.logo_url
          }}
          title="Editar Empresa"
          description="Atualize os dados da empresa."
          submitLabel="Salvar Alterações"
        />
      )}

      <DeleteTenantDialog
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTenantToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        tenant={tenantToDelete as any}
        isDeleting={isDeleting}
      />
    </div>
  );
}
